import React, { useMemo } from 'react';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Divider,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { ApiService } from 'api/services/ApiService';
import { useUserGroups } from 'dashboard409a/hooks/useUserGroups';
import { RootStateType } from 'dashboard409a/states/store';
import { getInitials } from 'utilities/getInitials';
import { modelFiltersStyles } from './ModelFilters.styles';
import { SelectedUserWithGroup, UserGroup } from '../UserGroups/UserGroups.types';

type GroupOption = UserGroup & { isGroupMarker: true };
type UserOrGroupOption = SelectedUserWithGroup | GroupOption;

const isGroupOption = (opt: UserOrGroupOption): opt is GroupOption =>
  'isGroupMarker' in opt && opt.isGroupMarker === true;

const getUserLabel = (user: DashboardSmallUser): string => {
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');
  return fullName || user.email || '';
};

export type GroupedUserAutocompleteDisplayItem =
  | { kind: 'group'; data: UserGroup }
  | { kind: 'user'; data: SelectedUserWithGroup };

export interface GroupedUserAutocompleteProps {
  value: SelectedUserWithGroup[];
  onChange: (newValue: SelectedUserWithGroup[]) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  label: string;
  placeholder: string;
  zoomLevel: number;
  textFieldSx?: SxProps<Theme>;
  disabled?: boolean;
  maxVisibleTags?: number;
  renderTagsOverride?: (displayItems: GroupedUserAutocompleteDisplayItem[], maxVisibleTags: number) => React.ReactNode;
  autocompleteRootSx?: SxProps<Theme>;
}

export const GroupedUserAutocomplete = ({
  value,
  onChange,
  inputValue,
  onInputChange,
  label,
  placeholder,
  zoomLevel,
  textFieldSx = modelFiltersStyles.autocompleteTextField,
  disabled = false,
  maxVisibleTags = 2,
  renderTagsOverride,
  autocompleteRootSx,
}: GroupedUserAutocompleteProps) => {
  const isSuperuser = useSelector((state: RootStateType) => state.user.is_superuser);
  const { groups, defaultGroup } = useUserGroups();

  const { data: staffUsers = [], isLoading } = useQuery({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return response.filter(u => !u.email?.includes('+')) || [];
    },
    staleTime: 60 * 60 * 1000,
  });

  const visibleGroups = isSuperuser ? groups : [];

  const defaultGroupIds = useMemo(
    () => new Set<number>(defaultGroup?.userIds ?? []),
    [defaultGroup]
  );

  const sortedStaffUsers = useMemo(() => {
    if (!defaultGroupIds.size) {
      return staffUsers;
    }
    return [...staffUsers].sort((a, b) => {
      const aIn = a.id !== undefined && defaultGroupIds.has(a.id);
      const bIn = b.id !== undefined && defaultGroupIds.has(b.id);
      if (aIn === bIn) {
        return 0;
      }
      return aIn ? -1 : 1;
    });
  }, [staffUsers, defaultGroupIds]);

  const options: UserOrGroupOption[] = [
    ...visibleGroups.map(g => ({ ...g, isGroupMarker: true as const })),
    ...sortedStaffUsers,
  ];

  const handleChange = (
    _e: unknown,
    newValue: UserOrGroupOption[],
    _reason: string,
    details?: { option: UserOrGroupOption }
  ) => {
    const clicked = details?.option;
    if (clicked && isGroupOption(clicked)) {
      const group = clicked;
      const groupUserIdSet = new Set(group.userIds);
      const allSelected =
        group.userIds.length > 0 && group.userIds.every(id => value.some(u => u.id === id));
      if (allSelected) {
        // Remove all users belonging to this group (regardless of how they were added)
        onChange(value.filter(u => !groupUserIdSet.has(u.id!)));
      } else {
        const existingIds = new Set(value.map(u => u.id));
        const toAdd: SelectedUserWithGroup[] = staffUsers
          .filter(u => groupUserIdSet.has(u.id!) && !existingIds.has(u.id))
          .map(u => ({ ...u, fromGroupId: group.id }));
        onChange([...value, ...toAdd]);
      }
    } else {
      onChange(newValue.filter((v): v is SelectedUserWithGroup => !isGroupOption(v)));
    }
  };

  // Build tag display: complete groups first, then individual users
  const completeGroups = visibleGroups.filter(
    g => g.userIds.length > 0 && g.userIds.every(id => value.some(u => u.id === id))
  );
  const completeGroupUserIds = new Set(completeGroups.flatMap(g => g.userIds));
  const individualUsers = value.filter(u => !completeGroupUserIds.has(u.id!));

  const displayItems: Array<{ kind: 'group'; data: UserGroup } | { kind: 'user'; data: SelectedUserWithGroup }> = [
    ...completeGroups.map(g => ({ kind: 'group' as const, data: g })),
    ...individualUsers.map(u => ({ kind: 'user' as const, data: u })),
  ];

  const hasGroups = visibleGroups.length > 0;

  return (
    <Autocomplete<UserOrGroupOption, true, false, false>
      size="small"
      multiple
      options={options}
      getOptionLabel={opt => (isGroupOption(opt) ? opt.name : getUserLabel(opt))}
      groupBy={hasGroups ? opt => (isGroupOption(opt) ? 'groups' : 'users') : undefined}
      slotProps={{
        popper: { sx: modelFiltersStyles.getAutocompletePopper(zoomLevel) },
      }}
      disableCloseOnSelect
      disabled={disabled}
      value={value as UserOrGroupOption[]}
      onChange={handleChange}
      onInputChange={(_e, v) => onInputChange(v)}
      inputValue={inputValue}
      loading={isLoading}
      limitTags={-1}
      filterOptions={(opts, state) => {
        const term = state.inputValue.trim().toLowerCase();
        if (!term) {
          return opts;
        }
        return opts.filter(opt =>
          isGroupOption(opt)
            ? opt.name.toLowerCase().includes(term)
            : getUserLabel(opt).toLowerCase().includes(term) ||
              (opt.email || '').toLowerCase().includes(term)
        );
      }}
      renderGroup={params => (
        <Box key={params.key}>
          {params.group === 'users' && (
            <Box>
              <Divider />
              <Typography
                variant="caption"
                sx={{ px: 1.5, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 500 }}
              >
                Users
              </Typography>
            </Box>
          )}
          {params.children}
        </Box>
      )}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          label={label}
          variant="outlined"
          placeholder={value.length === 0 ? placeholder : ''}
          sx={textFieldSx}
        />
      )}
      renderTags={() => {
        if (displayItems.length === 0) {
          return null;
        }
        if (renderTagsOverride) {
          return renderTagsOverride(displayItems, maxVisibleTags);
        }
        const visibleItems = displayItems.slice(0, maxVisibleTags);
        const remainingCount = displayItems.length - maxVisibleTags;
        return (
          <Box
            sx={{
              ...modelFiltersStyles.getTagsContainer(displayItems.length),
              maxWidth: `${maxVisibleTags * 28 + 40}px`,
            }}
          >
            {visibleItems.map((item, i) =>
              item.kind === 'group' ? (
                <Tooltip key={`g-${item.data.id}`} title={item.data.name} placement="top">
                  <Avatar
                    sx={{
                      ...modelFiltersStyles.avatar,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    <GroupsIcon sx={{ fontSize: '0.75rem' }} />
                  </Avatar>
                </Tooltip>
              ) : (
                <Box
                  key={`u-${item.data.id ?? i}`}
                  sx={{ position: 'relative', flexShrink: 0, display: 'inline-flex' }}
                >
                  <Avatar sx={modelFiltersStyles.avatar}>
                    {getInitials({ name: `${item.data.first_name} ${item.data.last_name}` })}
                  </Avatar>
                  {item.data.fromGroupId && (
                    <GroupsIcon
                      sx={{
                        position: 'absolute',
                        bottom: -2,
                        right: -3,
                        fontSize: '0.6rem',
                        color: 'primary.main',
                        bgcolor: 'background.paper',
                        borderRadius: '50%',
                        lineHeight: 1,
                      }}
                    />
                  )}
                </Box>
              )
            )}
            {remainingCount > 0 && (
              <Chip label={`+${remainingCount}`} size="small" sx={modelFiltersStyles.chip} />
            )}
          </Box>
        );
      }}
      renderOption={(props, option, { selected }) => {
        const { key, ...optionProps } = props;
        if (isGroupOption(option)) {
          const isChecked =
            option.userIds.length > 0 && option.userIds.every(id => value.some(u => u.id === id));
          return (
            <li key={option.id} {...optionProps} style={modelFiltersStyles.optionListItem}>
              <Checkbox style={modelFiltersStyles.optionCheckbox} checked={isChecked} />
              <GroupsIcon
                sx={{ fontSize: '1rem', mr: 0.5, color: 'text.secondary', flexShrink: 0 }}
              />
              {option.name} ({option.userIds.length})
            </li>
          );
        }
        return (
          <li key={option.id} {...optionProps} style={modelFiltersStyles.optionListItem}>
            <Checkbox style={modelFiltersStyles.optionCheckbox} checked={selected} />
            {getUserLabel(option)}
          </li>
        );
      }}
      sx={[modelFiltersStyles.autocompleteRoot, ...(Array.isArray(autocompleteRootSx) ? autocompleteRootSx : [autocompleteRootSx ?? {}])] as SxProps<Theme>}
      isOptionEqualToValue={(option, val) => {
        if (isGroupOption(option) || isGroupOption(val)) {
          return false;
        }
        return option.id === val.id;
      }}
    />
  );
};
